var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-image.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element";
import { BehaviorController } from "@adornis/chemistry/controllers/behavior-controller";
import { SizeController } from "@adornis/chemistry/controllers/size-controller";
import { event } from "@adornis/chemistry/decorators/event";
import { css } from "@adornis/chemistry/directives/css";
import "@adornis/chemistry/elements/components/x-flex";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BehaviorSubject, combineLatest, firstValueFrom, timer } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { AdornisFile } from "../db/files";
import { askForImage } from "./helper-methods";
var ElementsImage = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.styles = () => ({ ":host": { display: "block", position: "relative" } });
    this.mode = new BehaviorController(this, "cover");
    this.disabled = true;
    this.random = new BehaviorController(this, false);
    this["preview-src"] = new BehaviorController(this, "");
    this.value = new BehaviorController(
      this,
      void 0
    );
    this.fileEntity = AdornisFile;
    this.fileSize = new BehaviorController(this, 0);
    this.fileName = new BehaviorController(this, "");
    this.aspectratio = void 0;
    this.imageToUpload = null;
    this._imageToUploadPreview = new BehaviorSubject("");
    this.error = "";
    this.size = new SizeController(this);
    this.loaded = false;
    this.placeholder = "";
  }
  firstUpdated() {
    combineLatest([
      this.value,
      this["preview-src"],
      this.random,
      this.mode,
      this.size.observable.pipe(debounceTime(300)),
      this._imageToUploadPreview
    ]).pipe(
      filter(([, , , , size]) => !!size.width && !!size.height),
      takeUntil(this.disconnected)
    ).subscribe(async (args) => {
      const src = await this.loadSrc(...args);
      this.internalSource = src;
    });
  }
  async commit() {
    const id = await this.uploadToDatabase();
    if (typeof id === "string") {
      this.value.next(id);
      this._imageToUploadPreview.next("");
    }
    return id;
  }
  clear() {
    this.value.next(void 0);
    this.imageToUpload = null;
    this._imageToUploadPreview.next("");
    this.internalSource = void 0;
    this.fileSize.next(0);
    this.fileName.next("");
  }
  async preValidate() {
    await this.commit();
  }
  postValidate(errored) {
  }
  async loadSrc(val, previewSrc, random, mode, { width, height }, imageToUploadPreview) {
    if (imageToUploadPreview)
      return imageToUploadPreview;
    width *= window.devicePixelRatio;
    height *= window.devicePixelRatio;
    const requestedWidth = mode === "contain" ? Math.max(Math.ceil(width), Math.ceil(height)) + "" : Math.ceil(width) + "";
    const requestedHeight = ["width", "contain"].includes(mode) ? "" : Math.ceil(height) + "";
    if (random)
      return `https://picsum.photos/${requestedWidth}/${requestedHeight}`;
    const defaultDummy = `https://dummyimage.com/${requestedWidth}x${requestedHeight}/9e579e/2732c4.png`;
    if (val) {
      if (val.match(/^[a-f0-9]{24}$/))
        return this.fileEntity.getServeLink(val, "original", `${requestedWidth}x${requestedHeight}`);
      else
        return val;
    }
    if (this.placeholder)
      return this.placeholder;
    return previewSrc || defaultDummy + "&text=Dummy+Image";
  }
  async uploadToDatabase() {
    const file = this.imageToUpload;
    if (!file)
      return;
    if (!(file instanceof File))
      return alert("choose File");
    const imageID = await this.fileEntity.upload(file);
    await firstValueFrom(timer(1e3));
    return imageID;
  }
  handleFile(file) {
    if (!file)
      return;
    this.fileSize.next(file.size);
    this.fileName.next(file.name);
    this.imageToUpload = file;
    const reader = new FileReader();
    reader.onload = (e) => this._imageToUploadPreview.next(reader.result);
    reader.readAsDataURL(file);
  }
  render() {
    const wantsAspectRatio = !!this.aspectratio && !isNaN(+this.aspectratio);
    const dimensionStyle = {
      width: !!wantsAspectRatio ? this.offsetHeight * +this.aspectratio + "px" : "100%",
      height: !!wantsAspectRatio ? this.offsetWidth / +this.aspectratio + "px" : "100%"
    };
    return html`
      ${this.mode.value === "cover" || this.mode.value === "contain" ? html`
            <div
              id="img"
              ${css({
      backgroundImage: `url("${this.internalSource}")`,
      backgroundSize: this.mode.getValue(),
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      ...dimensionStyle
    })}
            >
              <img
                src="${this.internalSource}"
                @load=${() => this._loadedCallback()}
                ${css({ opacity: "0", width: "2px" })}
              />
            </div>
          ` : html`
            <img
              id="img"
              alt=${this.alt}
              @load=${() => this._loadedCallback()}
              src="${this.internalSource}"
              ${css(dimensionStyle)}
            />
          `}
      ${!this.disabled ? html`
            <x-flex
              ${css({ position: "absolute", inset: "0", cursor: "pointer" })}
              @click=${() => askForImage().then((file) => this.handleFile(file)).catch((e) => console.warn("There was a problem selecting a photo: ", e))}
            >
              ${this.value.value || this.imageToUpload ? nothing : html` <slot ${css({ position: "relative", zIndex: "1" })} name="placeholder"> </slot> `}
            </x-flex>
          ` : nothing}
    `;
  }
  _loadedCallback() {
    this.dispatchEvent(new Event("load"));
    this.loaded = true;
  }
};
__decorateClass([
  property()
], ElementsImage.prototype, "mode", 2);
__decorateClass([
  property({ type: Boolean })
], ElementsImage.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean })
], ElementsImage.prototype, "random", 2);
__decorateClass([
  property()
], ElementsImage.prototype, "preview-src", 2);
__decorateClass([
  event,
  property()
], ElementsImage.prototype, "value", 2);
__decorateClass([
  event,
  state()
], ElementsImage.prototype, "fileSize", 2);
__decorateClass([
  event,
  state()
], ElementsImage.prototype, "fileName", 2);
__decorateClass([
  property({ type: Number })
], ElementsImage.prototype, "aspectratio", 2);
__decorateClass([
  event,
  state()
], ElementsImage.prototype, "imageToUpload", 2);
__decorateClass([
  property()
], ElementsImage.prototype, "error", 2);
__decorateClass([
  state()
], ElementsImage.prototype, "internalSource", 2);
__decorateClass([
  state()
], ElementsImage.prototype, "size", 2);
__decorateClass([
  property({ reflect: true, type: Boolean })
], ElementsImage.prototype, "loaded", 2);
__decorateClass([
  property()
], ElementsImage.prototype, "placeholder", 2);
__decorateClass([
  property({ type: String })
], ElementsImage.prototype, "alt", 2);
ElementsImage = __decorateClass([
  customElement("x-image")
], ElementsImage);
export {
  ElementsImage
};
